import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import {
  useMessage,
  useMessages,
} from "@/features/messages/hooks/use-messages";
import { URGENCY_ORDER, type ClientMessage } from "@/features/messages/types";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { TrayIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCategoryLabels } from "./components/categories";
import type { MessageSort } from "./components/filters";
import {
  closedAt,
  MessageList,
  type MessageFilter,
} from "./components/message-list";
import { MessageView } from "./components/message-view";

/** The assignee filter's value for "nobody in particular". */
const UNASSIGNED = "__unassigned";

/**
 * The Messagerie: what the AI could not settle, waiting for a person — laid out
 * like the call history, the requests on the left and the selected one in full on
 * the right, its answer form included.
 *
 * The selection is the URL (`/messages/:messageId`), so a link to a request opens
 * it in place and the back button walks what was read.
 *
 * One menu orders the list and groups it at once: by urgency (the default — each
 * group by deadline, the one the urgency earns or what the customer was promised
 * when sooner), or by date, most recent first, under Today / Yesterday / This week
 * / month. The rows are all loaded, so all of it is done here.
 */
export function MessagesPage() {
  const { t } = useTranslation("messages");
  const navigate = useNavigate();
  const { messageId } = useParams<{ messageId: string }>();
  const clientId = useCurrentClient()?.client?.id || "";
  useTitle(t("title"));

  const [filter, setFilter] = useState<MessageFilter>("pending");
  const [search, setSearch] = useState("");
  const [assignee, setAssignee] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [sort, setSort] = useState<MessageSort>("urgency");
  // Checked rows, kept whole: one checked and then filtered out of view is still
  // acted on as it was when it was checked.
  const [checked, setChecked] = useState<Map<string, ClientMessage>>(
    () => new Map(),
  );
  // A new cut of the list is a new list: what was checked in the old one goes.
  const clearChecked = () => setChecked(new Map());
  const changeView = (next: MessageFilter) => {
    setFilter(next);
    clearChecked();
  };
  const { categories, labelOf } = useCategoryLabels();

  const pendingQuery = useMessages({ status: "pending" });
  const handledQuery = useMessages({ status: "handled" });

  const pending = useMemo(
    () => pendingQuery.data?.messages || [],
    [pendingQuery.data],
  );

  const handled = useMemo(
    () => handledQuery.data?.messages || [],
    [handledQuery.data],
  );

  const rows = filter === "handled" ? handled : pending;
  const query = filter === "handled" ? handledQuery : pendingQuery;

  // Whoever requests are assigned to, read off the requests themselves: the
  // filter only offers people somebody actually handed something to.
  const assigneeOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const m of [...pending, ...handled]) {
      if (m.assigned_member_id) {
        names.set(m.assigned_member_id, m.assigned_member_name || "—");
      }
    }
    if (names.size === 0) return [];
    return [
      ...[...names].map(([value, label]) => ({ value, label })),
      { value: UNASSIGNED, label: t("filters.unassigned") },
    ];
  }, [pending, handled, t]);

  // The tenant's categories, plus any a row was filed under before it was
  // removed: a filter must reach every request the list holds.
  const categoryKeys = useMemo(() => {
    const keys = categories.map((c) => c.key);
    for (const m of [...pending, ...handled]) {
      const key = m.category || "other";
      if (!keys.includes(key)) keys.push(key);
    }
    return keys;
  }, [categories, pending, handled]);
  const categoryOptions = categoryKeys.map((key) => ({
    value: key,
    label: labelOf(key),
  }));

  const needle = normalize(search.trim());
  const digits = needle.replace(/\D/g, "");
  const handledView = filter === "handled";
  const visible = useMemo(() => {
    const filtered = rows.filter(
      (m) =>
        (!category || (m.category || "other") === category) &&
        (!assignee ||
          (assignee === UNASSIGNED
            ? !m.assigned_member_id
            : m.assigned_member_id === assignee)) &&
        (!needle ||
          normalize(
            [
              m.subject,
              m.body,
              m.customer_name,
              m.phone_number,
              m.reply,
              m.assigned_member_name,
            ].join(" "),
          ).includes(needle) ||
          // A phone number is typed however it comes: compare digits alone.
          (digits.length >= 3 &&
            (m.phone_number || "").replace(/\D/g, "").includes(digits))),
    );
    // By date, newest first (a closed request by when it was closed); by urgency,
    // each group in its view's own order.
    if (sort === "recent") return [...filtered].sort((a, b) => ageOf(b, handledView) - ageOf(a, handledView));
    const within = handledView ? byClosing : byDeadline;
    const rank = (m: ClientMessage) => URGENCY_ORDER.indexOf(m.urgency);
    return [...filtered].sort((a, b) => rank(a) - rank(b) || within(a, b));
  }, [rows, sort, category, assignee, needle, digits, handledView]);

  const selection = {
    checked,
    check: (message: ClientMessage, on: boolean) =>
      setChecked((current) => {
        const next = new Map(current);
        if (on) next.set(message.id, message);
        else next.delete(message.id);
        return next;
      }),
    checkAll: (on: boolean) =>
      setChecked(on ? new Map(visible.map((m) => [m.id, m])) : new Map()),
    clear: clearChecked,
  };

  // The selected request is read from the lists already loaded; only a link to
  // one that neither holds fetches it on its own.
  const listed = useMemo(
    () => [...pending, ...handled].find((m) => m.id === messageId),
    [pending, handled, messageId],
  );
  const listsReady = pendingQuery.isSuccess && handledQuery.isSuccess;
  const single = useMessage(messageId || "", {
    enabled: !!messageId && listsReady && !listed,
  });
  const selected = listed || single.data?.message;

  // A link that lands on a closed request opens the view that lists it, so the
  // row is highlighted rather than missing. Keyed on the id alone: a request
  // closed while it is open stays where it was read.
  const listedStatus = listed?.status;
  useEffect(() => {
    if (listedStatus === "handled") setFilter("handled");
  }, [listed?.id]);

  const open = (message: ClientMessage) =>
    navigate(`/${clientId}/messages/${message.id}`);
  const close = () => navigate(`/${clientId}/messages`);

  // Like a mail client archiving a message: the request just closed leaves the
  // list, and the one below it (else above) opens in its place.
  const openNext = (done: ClientMessage) => {
    const index = visible.findIndex((m) => m.id === done.id);
    if (index < 0) return;
    const next = visible[index + 1] || visible[index - 1];
    if (next) open(next);
    else close();
  };

  return (
    // The call history's frame (`calls/index.tsx`): flush with the application
    // frame, one pane at a time on a phone.
    <div className="-mx-4 flex h-[calc(100dvh-3rem)] overflow-hidden bg-white md:-mx-8 md:h-[calc(100dvh-1rem)] md:rounded-lg dark:bg-slate-950">
      <div
        className={`min-h-0 w-full flex-col border-r md:flex md:w-[22rem] md:shrink-0 lg:w-[26rem] ${messageId ? "hidden" : "flex"}`}
      >
        <MessageList
          filter={filter}
          onFilterChange={changeView}
          counts={{ pending: pending.length, handled: handled.length }}
          search={search}
          onSearchChange={setSearch}
          assignee={assignee}
          onAssigneeChange={setAssignee}
          assigneeOptions={assigneeOptions}
          category={category}
          onCategoryChange={setCategory}
          categoryOptions={categoryOptions}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          messages={visible}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={query.refetch}
          filtering={!!needle || !!assignee || !!category}
          selectedId={messageId}
          onSelect={open}
        />
      </div>

      <div
        className={`min-h-0 min-w-0 flex-1 flex-col ${messageId ? "flex" : "hidden md:flex"}`}
      >
        {!messageId ? (
          <EmptyState
            compact
            icon={TrayIcon}
            title={t("list.selectPrompt")}
            className="h-full"
          />
        ) : selected ? (
          <MessageView
            message={selected}
            onBack={close}
            onResolved={openNext}
          />
        ) : single.isError ? (
          <ErrorState compact onRetry={single.refetch} className="h-full" />
        ) : (
          <div className="space-y-4 p-6">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Soonest deadline first; rows with none after them, newest first. */
const byDeadline = (a: ClientMessage, b: ClientMessage) => {
  if (!!a.due_at !== !!b.due_at) return a.due_at ? -1 : 1;
  if (a.due_at && b.due_at) return a.due_at - b.due_at;
  return (b.created_at || 0) - (a.created_at || 0);
};

/** When a request counts as arrived: its closing in the handled view. */
const ageOf = (m: ClientMessage, handledView: boolean) =>
  handledView ? closedAt(m) : m.created_at || 0;

/** Last closed first. */
const byClosing = (a: ClientMessage, b: ClientMessage) =>
  closedAt(b) - closedAt(a);

/** Case and accents aside: "Résa" finds "resa". */
const normalize = (text: string) =>
  text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
